"use client";

import React, { useMemo, useState } from "react";
import { BarChart, Callout, Card, LineChart, Title } from "@tremor/react";
import { Empty, Skeleton, Switch, Tooltip } from "antd";
import dayjs from "dayjs";

import { type ProductPrice } from "@fitness-app/data-models/entities/Product";

import {
  useProductRenewalCohortStats,
  type RenewalCohortData,
} from "~/modules/Products/Product/ProductDetails/hooks/useProductRenewalCohortStats";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import { useAppSelector } from "~/store/initializeStore";

interface ProductCohortRetentionStatsProps {
  productId: string;
  prices: ProductPrice[];
}

const getPriceLabel = (price: ProductPrice | undefined, fallbackId: string) => {
  if (!price) {
    return fallbackId;
  }

  if (price.customLabel) {
    return price.customLabel;
  }

  const promoConfig = price.config?.showAsPromo;
  if (promoConfig && typeof promoConfig === "object" && "promoTagText" in promoConfig && promoConfig.promoTagText) {
    return promoConfig.promoTagText;
  }

  if (price.unit_amount != null) {
    return getPriceFormatter(price.currency).format(price.unit_amount / 100);
  }

  return price.id;
};

const getTopPackage = (counts: Record<string, number>, resolveLabel: (priceId: string) => string) => {
  const [topPriceId, topCount] = Object.entries(counts).sort(([, a], [, b]) => b - a)[0] ?? [];
  if (!topPriceId || !topCount) {
    return "—";
  }
  return `${resolveLabel(topPriceId)} (${topCount})`;
};

const revenueFormatter = getPriceFormatter("PLN");

export const ProductCohortRetentionStats = ({
  productId,
  prices,
}: ProductCohortRetentionStatsProps): React.ReactElement => {
  const { data, isLoading, isError } = useProductRenewalCohortStats(productId);
  const [splitUpgrades, setSplitUpgrades] = useState(false);
  const allProducts = useAppSelector((store) => store.products.productsListData);

  const priceById = useMemo(() => {
    return prices.reduce<Record<string, ProductPrice>>((acc, price) => {
      acc[price.id] = price;
      return acc;
    }, {});
  }, [prices]);

  // An upgrade is a purchase of a DIFFERENT product, so the package it was bought on is not
  // in this product's price list — resolving it only here left the raw price id on screen.
  const foreignPriceById = useMemo(() => {
    const index = new Map<string, { price: ProductPrice; productName: string }>();

    for (const product of allProducts) {
      if (product.id === productId) {
        continue;
      }

      for (const price of product.prices ?? []) {
        if (!index.has(price.id)) {
          index.set(price.id, { price, productName: product.name });
        }
      }
    }

    return index;
  }, [allProducts, productId]);

  const resolveLabel = useMemo(() => {
    return (priceId: string) => {
      const ownPrice = priceById[priceId];
      if (ownPrice) {
        return getPriceLabel(ownPrice, priceId);
      }

      const foreign = foreignPriceById.get(priceId);

      return foreign ? `${foreign.productName} · ${getPriceLabel(foreign.price, priceId)}` : priceId;
    };
  }, [priceById, foreignPriceById]);

  const cohorts: RenewalCohortData[] = data?.cohorts ?? [];

  // The repurchase/upgrade split arrived after these stats started being cached, so a product
  // whose cohorts have not been recomputed since still serves the combined number only.
  const splitAvailable = cohorts.some((cohort) => cohort.customersWhoUpgraded != null);
  const showSplit = splitUpgrades && splitAvailable;

  const cohortSeries = useMemo(
    () =>
      cohorts.map((cohort) => ({
        cohortLabel: cohort.cohortLabel,
        Klienci: cohort.totalCustomers,
        "Wygasłe dostępy": cohort.customersWithExpiredAccess,
        ...(showSplit
          ? {
              "Ponowne zakupy": cohort.customersWhoRepurchased ?? 0,
              "Upgrade'y": cohort.customersWhoUpgraded ?? 0,
            }
          : { Odnowienia: cohort.customersWhoRenewed }),
      })),
    [cohorts, showSplit],
  );

  const renewalRateSeries = useMemo(
    () =>
      cohorts.map((cohort) => ({
        cohort: cohort.cohortLabel,
        renewalRate: cohort.renewalRate,
      })),
    [cohorts],
  );

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (isError) {
    return (
      <Callout title="Błąd" color="rose">
        Nie udało się pobrać statystyk kohort. Spróbuj ponownie później.
      </Callout>
    );
  }

  if (!data) {
    return (
      <Callout title="Brak danych" color="amber">
        Statystyki kohort nie zostały jeszcze wygenerowane dla tego produktu. Pojawią się po najbliższym przeliczeniu
        (job uruchamia się co 6 godzin).
      </Callout>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Tooltip
          title={
            splitAvailable
              ? "Odnowienie = ponowny zakup tego produktu, upgrade = zakup innego produktu w oknie odnowienia."
              : "Rozbicie pojawi się po najbliższym przeliczeniu statystyk dla tego produktu."
          }
        >
          <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            Rozdziel ponowne zakupy i upgrade'y
            <Switch size="small" disabled={!splitAvailable} checked={showSplit} onChange={setSplitUpgrades} />
          </span>
        </Tooltip>
      </div>

      <Card>
        <Title>
          {showSplit
            ? "Klienci, wygasłe dostępy, ponowne zakupy i upgrade'y (wg kohorty pierwszego zakupu)"
            : "Klienci, wygasłe dostępy i odnowienia (wg kohorty pierwszego zakupu)"}
        </Title>
        {cohortSeries.length ? (
          <BarChart
            className="mt-6 h-72"
            data={cohortSeries}
            index="cohortLabel"
            categories={
              showSplit
                ? ["Klienci", "Wygasłe dostępy", "Ponowne zakupy", "Upgrade'y"]
                : ["Klienci", "Wygasłe dostępy", "Odnowienia"]
            }
            colors={showSplit ? ["indigo", "slate", "emerald", "amber"] : ["indigo", "slate", "emerald"]}
            valueFormatter={(value) => (value != null ? Intl.NumberFormat("pl-PL").format(value) : "0")}
            showLegend={true}
            allowDecimals={false}
            showGridLines={true}
            showYAxis={true}
            yAxisWidth={80}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <Title>Współczynnik odnowień (odnowienia / wygasłe dostępy)</Title>
          {renewalRateSeries.length ? (
            <LineChart
              className="mt-6 h-72"
              data={renewalRateSeries}
              index="cohort"
              categories={["renewalRate"]}
              colors={["amber"]}
              valueFormatter={(value) => `${value?.toFixed(0) ?? 0}%`}
              showLegend={false}
              yAxisWidth={60}
              minValue={0}
              maxValue={100}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>

        <Card>
          <Title>
            {showSplit
              ? "Przychody: pierwszy zakup vs ponowne zakupy vs upgrade'y"
              : "Przychody: pierwszy zakup vs odnowienia"}
          </Title>
          {cohorts.length ? (
            <BarChart
              className="mt-6 h-72"
              data={cohorts.map((cohort) => ({
                cohortLabel: cohort.cohortLabel,
                "Pierwszy zakup": cohort.initialRevenue / 100,
                ...(showSplit
                  ? {
                      "Ponowne zakupy": (cohort.renewalRevenue - (cohort.upgradeRevenue ?? 0)) / 100,
                      "Upgrade'y": (cohort.upgradeRevenue ?? 0) / 100,
                    }
                  : { Odnowienia: cohort.renewalRevenue / 100 }),
              }))}
              index="cohortLabel"
              categories={
                showSplit ? ["Pierwszy zakup", "Ponowne zakupy", "Upgrade'y"] : ["Pierwszy zakup", "Odnowienia"]
              }
              colors={showSplit ? ["indigo", "emerald", "amber"] : ["indigo", "emerald"]}
              valueFormatter={(value) => revenueFormatter.format(value ?? 0)}
              showLegend={true}
              stack={true}
              showGridLines={true}
              showYAxis={true}
              yAxisWidth={90}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
          )}
        </Card>
      </div>

      <Card>
        <Title>Kohorty i pakiety</Title>
        {cohorts.length ? (
          <div className="mt-4 divide-y divide-gray-200 dark:divide-gray-800">
            {cohorts.map((cohort) => (
              <div key={cohort.cohortMonth} className="grid grid-cols-1 gap-2 py-3 md:grid-cols-7 md:items-center">
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Kohorta</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{cohort.cohortLabel}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Osoby</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{cohort.totalCustomers}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Wygasłe dostępy</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                    {cohort.customersWithExpiredAccess}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Odnowienia</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                    {cohort.customersWhoRenewed} ({cohort.renewalRate}%)
                  </p>
                  {showSplit ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ponowne {cohort.customersWhoRepurchased ?? 0} · upgrade {cohort.customersWhoUpgraded ?? 0}
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Śr. dni do odnowienia</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                    {cohort.avgDaysToRenewal ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Startowy pakiet</p>
                  <p className="text-sm text-gray-900 dark:text-gray-50">
                    {getTopPackage(cohort.initialPackages, resolveLabel)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    {showSplit ? "Pakiet przy upgradzie" : "Pakiet przy odnowieniu"}
                  </p>
                  <p className="text-sm text-gray-900 dark:text-gray-50">
                    {getTopPackage(showSplit ? (cohort.upgradePackages ?? {}) : cohort.renewalPackages, resolveLabel)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
        )}
      </Card>

      <p className="text-right text-xs text-gray-500 dark:text-gray-400">
        Ostatnia aktualizacja: {dayjs(data.lastUpdated).format("DD.MM.YYYY HH:mm")}
      </p>
    </div>
  );
};
